import classNames from "classnames";
import { useDispatch } from "react-redux";
import { updateColorImage } from "../../../../../store/slices/viewSlice";
import { useAppSelector } from "../../../../../../hooks/reduxHooks";
import { useState, useEffect, useRef } from "react";
import {
  checkAndCreateSessionIdFromCookie,
  saveSelectedColorsToCookie,
  getTempCart,
} from "../../../../../../utils/utils";
import { setRefresher } from "../../../../../store/slices/productSlice";

// Revert to using any types to match the parent component
const VariantColorPicker = ({ colorChosen, setColorChosen, colors }: any) => {
  const dispatch = useDispatch();
  const [selectedColors, setSelectedColors] = useState<any[]>([]);
  const productImages = useAppSelector((state) => state.product.product_images);
  const refresher = useAppSelector((state) => state.product.refresher);
  const product_details = useAppSelector(
    (state) => state.product.product_details
  );
  const initialized = useRef(false);
  // Flag to prevent updating tempCart during initialization
  const skipNextTempCartUpdate = useRef(false);
  
  // Save selected colors to cookie/cart whenever they change
  useEffect(() => {
    // Skip the first update if we just initialized from localStorage
    if (skipNextTempCartUpdate.current) {
      skipNextTempCartUpdate.current = false;
      return;
    }
    
    if (selectedColors.length > 0 && initialized.current) {
      const uniqueId = checkAndCreateSessionIdFromCookie();
      // Extract only the ids from the selectedColors array
      const colorIds = selectedColors.map(c => c.id);
      
      console.log("Saving selected colors to cookie:", colorIds);
      
      // Save to cookie with the exact format expected by the pricing component
      saveSelectedColorsToCookie(
        uniqueId,
        colorIds,
        product_details?.CustomizedProductID || ""
      );
    }
  }, [selectedColors, product_details]);
  
  // Handle UI refreshes separately to avoid infinite loops
  const refreshUI = () => {
    if (initialized.current) {
      dispatch(setRefresher(!refresher));
    }
  };
  
  // Initial setup when component loads
  useEffect(() => {
    // Only run once
    if (initialized.current) {
      return;
    }
    
    console.log("Initial setup running");
    console.log("Colors available:", colors);
    
    let colorsFromCookies: any[] = [];
    let shouldSelectFirstColor = true;
    
    // Try to load colors from cookies
    const cartData = getTempCart();
    if (cartData) {
      try {
        const parsedCart = JSON.parse(cartData);
        if (parsedCart.selectedColors && parsedCart.selectedColors.length > 0) {
          console.log("Found selectedColors in cart:", parsedCart.selectedColors);
          
          // Find matching color objects
          colorsFromCookies = colors.filter((color: any) => 
            parsedCart.selectedColors.includes(color.id)
          );
          
          console.log("Matching color objects from cookies:", colorsFromCookies);
          
          if (colorsFromCookies.length > 0) {
            shouldSelectFirstColor = false;
            skipNextTempCartUpdate.current = true; // Skip next tempCart update
            setSelectedColors(colorsFromCookies);
            
            // If colorChosen not set, use first color from cookies
            if (!colorChosen && colorsFromCookies[0]) {
              console.log("Setting colorChosen from cookies:", colorsFromCookies[0]);
              setColorChosen(colorsFromCookies[0]);
              updateProductImage(colorsFromCookies[0]);
            }
          }
        }
      } catch (e) {
        console.error("Error parsing cart data:", e);
      }
    }
    
    // If no colors from cookies and colors array exists, select first one
    if (shouldSelectFirstColor && colors && colors.length > 0) {
      console.log("Selecting first color:", colors[0]);
      
      const firstColor = colors[0];
      skipNextTempCartUpdate.current = true; // Skip next tempCart update
      setSelectedColors([firstColor]);
      setColorChosen(firstColor);
      updateProductImage(firstColor);
      
      // Save to cookie directly instead of relying on the effect
      const uniqueId = checkAndCreateSessionIdFromCookie();
      saveSelectedColorsToCookie(
        uniqueId,
        [firstColor.id],
        product_details?.CustomizedProductID || ""
      );
    }
    
    initialized.current = true;
    // After initialization, trigger a UI refresh once
    setTimeout(() => refreshUI(), 0);
  }, [colors, product_details, colorChosen, setColorChosen]);

  const handleClick = (color: any) => {
    // Check if color is already selected
    const isSelected = selectedColors.some(c => c.id === color.id);
    
    if (isSelected) {
      // Remove from selection if already selected
      const updatedColors = selectedColors.filter(c => c.id !== color.id);
      setSelectedColors(updatedColors);
      
      // If we're removing the current chosen color, set a new one if available
      if (color.id === colorChosen?.id && updatedColors.length > 0) {
        setColorChosen(updatedColors[0]);
        updateProductImage(updatedColors[0]);
      }
    } else {
      // Add to selection if not already selected
      const updatedColors = [...selectedColors, color];
      setSelectedColors(updatedColors);
      
      // Update the current chosen color
      setColorChosen(color);
      updateProductImage(color);
    }
    
    // Refresh the UI after state updates
    setTimeout(() => refreshUI(), 10);
  };
  
  const updateProductImage = (color: any) => {
    if (!color) return;
    
    const productImage = productImages.find(
      (item: any) => item.image_id === color.image_id
    );
    
    if (productImage) {
      dispatch(
        updateColorImage({
          id: productImage.id,
          url: productImage.src,
          name: "Test8.jpg",
          type: "image/jpeg",
          size: '496.69 KB / 11.25x9"',
          resolution: "3375 x 2700 @ 300 dpi",
          createdDate: productImage.created_at,
          ...productImage,
        })
      );
    }
  };

  return (
    <>
      <div className="font-bold my-1">Color ({selectedColors.length} selected)</div>
      <div className="flex flex-wrap gap-1">
        {colors.map((color: any) => {
          const isSelected = selectedColors.some(c => c.id === color.id);
          const isCurrentColor = colorChosen && colorChosen.id === color.id;
          
          return (
            <div
              key={color.option1}
              onClick={() => handleClick(color)}
              className={classNames(
                "w-8 h-8 p-[1px] border rounded-full cursor-pointer",
                {
                  "border-[#B4B4B4]": !isSelected,
                  "border-green-400 border-2": isSelected,
                  "ring-2 ring-blue-400": isCurrentColor && isSelected,
                }
              )}
            >
              <div
                className="w-full h-full rounded-full"
                data-color={color.option1}
                style={{ backgroundColor: color.option1 }}
              ></div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default VariantColorPicker;
